import {useMemo, useState} from "react";
import {Button, Stack, Typography} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import {appStyles} from "../../../../AppStyles.ts";
import {StyledInputBase, StyledSearch, StyledSearchIconWrapper} from "../../../AppStyledComponents.tsx";
import type {URLLink} from "../../../../sdo/URLLink.ts";
import WebReferenceCard from "../../intel/panels/web/WebReferenceCard.tsx";

let nextId = 100;

function createEmptyURLLink(): URLLink {
    return {
        uid: String(nextId++),
        title: "",
        sourceURL: "",
        summary: "",
    };
}

// TODO: Replace with API call to load URLs from AResourceProfileManager.readURLs()
function buildTestData(): URLLink[] {
    return [
        {
            uid: "1",
            title: "Source document — original publication",
            sourceURL: "https://docs.example.com/publications/2026/quarterly-report-q1.pdf",
            summary: "Original publication of the quarterly report from which this resource was extracted. Contains the full author byline and publication metadata.",
        },
        {
            uid: "2",
            title: "Mirror copy on partner archive",
            sourceURL: "https://archive.partner.example.org/mirror/quarterly-report-q1.pdf",
            summary: "Mirrored copy preserved on the partner research archive. Useful as a fallback if the primary source becomes unavailable.",
        },
        {
            uid: "3",
            title: "Editor's commentary thread",
            sourceURL: "https://forum.example.com/threads/2026-q1-report-discussion",
            summary: "",
        },
    ];
}

function UXResourceWebRefsPanel() {
    const [links, setLinks] = useState<URLLink[]>(buildTestData);
    const [filterText, setFilterText] = useState("");
    const [newLinkUid, setNewLinkUid] = useState<string | null>(null);

    const filtered = useMemo(() => {
        if (!filterText.trim()) return links;
        const q = filterText.toLowerCase();
        return links.filter((l) => JSON.stringify(l).toLowerCase().includes(q));
    }, [links, filterText]);

    const handleDelete = (uid: string) => {
        // TODO: Replace with API call to AResourceProfileManager.deleteURL()
        setLinks((prev) => prev.filter((l) => l.uid !== uid));
        if (newLinkUid === uid) setNewLinkUid(null);
    };

    const handleUpdate = (updated: URLLink) => {
        // TODO: Replace with API call to AResourceProfileManager.saveURL()
        setLinks((prev) => prev.map((l) => l.uid === updated.uid ? updated : l));
    };

    const handleNew = () => {
        const newLink = createEmptyURLLink();
        setLinks((prev) => [newLink, ...prev]);
        setNewLinkUid(newLink.uid);
    };

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            {/* Title */}
            <Stack direction="row" padding={1} justifyContent="center">
                <Typography sx={appStyles.panelSectionTitle}>Web References</Typography>
            </Stack>

            {/* Filter bar */}
            <Stack direction="row" spacing={1} padding={1} alignItems="center" justifyContent="center" sx={{position: "relative"}}>
                <StyledSearch>
                    <StyledSearchIconWrapper>
                        <SearchIcon sx={{fontSize: 16, color: "#7093a0"}}/>
                    </StyledSearchIconWrapper>
                    <StyledInputBase
                        placeholder="Filter references..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </StyledSearch>

                <Typography sx={appStyles.panelFilterCount}>
                    Showing {filtered.length} of {links.length}
                </Typography>

                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon/>}
                    onClick={handleNew}
                    sx={{...appStyles.panelActionButton, position: "absolute", right: 8}}
                >
                    New Reference
                </Button>
            </Stack>

            {/* Web reference cards list */}
            <Stack direction="column" spacing={1} sx={{...appStyles.defaultPanelCardsWrapper, padding: "8px", alignItems: "stretch"}}>
                {filtered.map((link) => (
                    <WebReferenceCard
                        key={link.uid}
                        link={link}
                        onDelete={handleDelete}
                        onUpdate={handleUpdate}
                        startInEditMode={link.uid === newLinkUid}
                    />
                ))}
                {filtered.length === 0 && (
                    <Typography sx={{
                        color: "rgba(242, 245, 250, 0.4)",
                        fontSize: "0.80rem",
                        textAlign: "center",
                        padding: "24px",
                    }}>
                        No web references match the current filter.
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}

export default UXResourceWebRefsPanel;
